// API Configuration and Service
const API_BASE_URL = 'http://localhost:8000/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth token from localStorage
  getToken() {
    return localStorage.getItem('auth_token');
  }

  // Set auth token in localStorage
  setToken(token) {
    localStorage.setItem('auth_token', token);
  }

  // Remove auth token from localStorage
  removeToken() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }

  // Get user data from localStorage
  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Set user data in localStorage
  setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getToken();
  }

  // Build headers with auth token
  getHeaders(includeAuth = false, contentType = 'application/json') {
    const headers = {
      'Accept': 'application/json',
    };

    if (contentType) {
      headers['Content-Type'] = contentType;
    }

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(options.auth || false),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      // Handle token expiration or invalid token
      if (response.status === 401) {
        this.removeToken();
        // Redirect to login or emit event
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }

      return {
        success: response.ok,
        status: response.status,
        data,
      };
    } catch (error) {
      console.error('API Request Error:', error);
      return {
        success: false,
        status: 0,
        data: {
          message: 'Network error. Please check your connection.',
        },
      };
    }
  }

  // POST request
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  // Auth API Methods
  async register(userData) {
    const response = await this.post('/register', userData);
    
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    
    return response;
  }

  async login(credentials) {
    const response = await this.post('/login', credentials);
    
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    
    return response;
  }

   async loginWithMobile(credentials) {
    const response = await this.post('/login/mobile', credentials);
    
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    
    return response;
  }

  async logout() {
    const response = await this.post('/logout', {}, { auth: true });
    this.removeToken();
    return response;
  }

  async forgotPassword(email) {
    return this.post('/forgot-password', { email });
  }

  async verifyOtp(email, otp) {
    return this.post('/verify-otp', { email, otp });
  }

   async verifyMobileOtp(mobile, otp) {
    return this.post('/login/otp/mobile/verify', { mobile, otp });
  }

  async resetPassword(email,otp, password, password_confirmation) {
    return this.post('/reset-password', {
      email,
      otp,
      password,
      password_confirmation,
    });
  }

  async getAuthenticatedUser() {
    return this.get('/user', { auth: true });
  }

  // Social Auth Methods
  redirectToGoogle() {
    window.location.href = `${this.baseURL}/auth/google`;
  }

  redirectToGithub() {
    window.location.href = `${this.baseURL}/auth/github`;
  }

  // Handle social auth callback
  handleSocialCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      this.setToken(token);
      // Fetch user data
      this.getAuthenticatedUser().then(response => {
        if (response.success) {
          this.setUser(response.data.user);
          window.location.href = '/dashboard';
        }
      });
    }
  }

  // Additional API methods can be added here
  async sendOTPForLogin(mobile){
    return this.post('/login/otp/mobile/send', { mobile});
  }

  async connectWhatsApp(whatsappNumber) {
    return this.put('/user/connect-whatsapp', { 
      whatsapp_number: whatsappNumber, 
      whatsapp_enabled: true 
    }, { auth: true },{ token: this.getToken() });
  }

  async updateWhatsAppPreference(enabled) {
    return this.put('/user/whatsapp-preference', { 
      whatsapp_enabled: enabled 
    }, { auth: true },{ token: this.getToken() });
  }

  async updateUserProfile(profileData) {
    return this.put('/user/update-profile', profileData, { auth: true });
  }

  async fetchDeliveryAddresses() {
    return this.get('/user/delivery-addresses', { auth: true });
  }

  async addDeliveryAddress(addressData) {
    return this.post('/user/delivery-addresses', addressData, { auth: true });
  }

  async updateDeliveryAddress(addressId, addressData) {
    return this.put(`/user/delivery-addresses/${addressId}`, addressData, { auth: true });
  }

  async deleteDeliveryAddress(addressId) {
    return this.delete(`/user/delivery-addresses/${addressId}`, { auth: true });
  }

  async setDefaultDeliveryAddress(addressId) {
    return this.put(`/user/delivery-addresses/${addressId}/set-default`, {}, { auth: true });
  }

    // ============================================
  // CART API METHODS
  // ============================================
  
  // Get user cart from backend
  async getCart() {
    return this.get('/cart', { auth: true });
  }

  // Add item to cart
  async addToCart(productData) {
    return this.post('/cart', {
      product_id: productData.id,
      quantity: productData.quantity || 1,
      price: productData.price
    }, { auth: true });
  }

  // Update cart item quantity
  async updateCartItem(cartItemId, quantity) {
    return this.put(`/cart/${cartItemId}`, {
      quantity
    }, { auth: true });
  }

  // Remove item from cart
  async removeFromCart(cartItemId) {
    return this.delete(`/cart/${cartItemId}`, { auth: true });
  }

  // Clear entire cart
  async clearCart() {
    return this.delete('/cart', { auth: true });
  }

  // Get cart total
  async getCartTotal() {
    return this.get('/cart/total', { auth: true });
  }

  // Sync local cart with backend (for migration from localStorage)
  async syncCart(cartItems) {
    return this.post('/cart/sync', {
      items: cartItems.map(item => ({
        product_id: item.id,
        quantity: item.quantity,
        price: item.price
      }))
    }, { auth: true });
  }

  // ============================================
  // ORDERS API METHODS
  // ============================================
  
  // Create order from cart
  async createOrder(orderData) {
    console.log("API Order Data:"+JSON.stringify(orderData));
   
    return this.post('/orders', {      
      delivery_address_id: orderData.deliveryAddressesId,
      delivery_slot: ''+orderData.deliverySlot+'',
      payment_method: ''+orderData.paymentMethod+'',
      notes: ''+orderData.notes+'' || ''
    }, { auth: true });
  }

  // Get user orders
  async getMyOrders(page = 1, perPage = 10, filters = {}) {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    return this.get(`/orders?${params.toString()}`, { auth: true });
  }

  // Get single order details
  async getOrderById(orderId) {
    return this.get(`/orders/${orderId}`, { auth: true });
  }

  // Cancel order
  async cancelOrder(orderId, reason) {
    return this.put(`/orders/${orderId}/cancel`, {
      reason: reason || 'Customer requested cancellation'
    }, { auth: true });
  }

  // Track order
  async trackOrder(orderId) {
    return this.get(`/orders/${orderId}/track`, { auth: true });
  }

  // Rate order/product
  async rateOrder(orderId, ratingData) {
    return this.post(`/orders/${orderId}/rate`, {
      rating: ratingData.rating,
      review: ratingData.review || '',
      product_id: ratingData.productId
    }, { auth: true });
  }

  // ============================================
  // PRODUCTS API METHODS
  // ============================================
  
  // Get all products
  async getProducts(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = queryParams ? `/products?${queryParams}` : '/products';
    return this.get(endpoint);
  }

  // Get single product
  async getProductById(productId) {
    return this.get(`/products/${productId}`);
  }

  // Search products
  async searchProducts(query) {
    return this.get(`/products/search?q=${encodeURIComponent(query)}`);
  }

  // Get product by category
  async getProductsByCategory(category) {
    return this.get(`/products/category/${category}`);
  }

  // ============================================
  // SUBSCRIPTION API METHODS
  // ============================================
  
  // Create subscription
  async createSubscription(subscriptionData) {
    return this.post('/subscriptions', {
      product_id: subscriptionData.productId,
      plan_id: subscriptionData.planId,
      delivery_slot: subscriptionData.deliverySlot,
      delivery_address_id: subscriptionData.addressId,
      start_date: subscriptionData.startDate
    }, { auth: true });
  }

  // Get user subscriptions
  async getMySubscriptions() {
    return this.get('/subscriptions/my-subscriptions', { auth: true });
  }

  // Pause subscription
  async pauseSubscription(subscriptionId, pauseUntil) {
    return this.put(`/subscriptions/${subscriptionId}/pause`, {
      pause_until: pauseUntil
    }, { auth: true });
  }

  // Resume subscription
  async resumeSubscription(subscriptionId) {
    return this.put(`/subscriptions/${subscriptionId}/resume`, {}, { auth: true });
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId, reason) {
    return this.put(`/subscriptions/${subscriptionId}/cancel`, {
      reason: reason || 'Customer requested cancellation'
    }, { auth: true });
  }

  // ============================================
  // FAVORITES/WISHLIST API METHODS
  // ============================================
  
  // Get favorites
  async getFavorites() {
    return this.get('/favorites', { auth: true });
  }

  // Add to favorites
  async addToFavorites(productId) {
    return this.post('/favorites/add', {
      product_id: productId
    }, { auth: true });
  }

  // Remove from favorites
  async removeFromFavorites(productId) {
    return this.delete(`/favorites/remove/${productId}`, { auth: true });
  }

  // ============================================
  // NOTIFICATIONS API METHODS
  // ============================================
  
  // Get notifications
  async getNotifications() {
    return this.get('/notifications', { auth: true });
  }

  // Mark notification as read
  async markNotificationRead(notificationId) {
    return this.put(`/notifications/${notificationId}/read`, {}, { auth: true });
  }

  // Mark all notifications as read
  async markAllNotificationsRead() {
    return this.put('/notifications/mark-all-read', {}, { auth: true });
  }

  // Delete notification
  async deleteNotification(notificationId) {
    return this.delete(`/notifications/${notificationId}`, { auth: true });
  }

  // ============================================
  // PAYMENT METHODS API
  // ============================================
  
  // Get saved payment methods
  async getPaymentMethods() {
    return this.get('/payment-methods', { auth: true });
  }

  // Add payment method
  async addPaymentMethod(paymentData) {
    return this.post('/payment-methods/add', {
      type: paymentData.type, // 'card', 'upi', 'wallet'
      details: paymentData.details,
      is_default: paymentData.isDefault || false
    }, { auth: true });
  }

  // Update payment method
  async updatePaymentMethod(paymentMethodId, paymentData) {
    return this.put(`/payment-methods/${paymentMethodId}`, paymentData, { auth: true });
  }

  // Delete payment method
  async deletePaymentMethod(paymentMethodId) {
    return this.delete(`/payment-methods/${paymentMethodId}`, { auth: true });
  }

  // Set default payment method
  async setDefaultPaymentMethod(paymentMethodId) {
    return this.put(`/payment-methods/${paymentMethodId}/set-default`, {}, { auth: true });
  }

  // ============================================
  // HELP & SUPPORT API METHODS
  // ============================================
  
  // Get FAQ
  async getFAQ() {
    return this.get('/support/faq');
  }

  // Submit support ticket
  async submitSupportTicket(ticketData) {
    return this.post('/support/ticket', {
      subject: ticketData.subject,
      category: ticketData.category,
      description: ticketData.description,
      priority: ticketData.priority || 'medium'
    }, { auth: true });
  }

  // Get user tickets
  async getMyTickets() {
    return this.get('/support/my-tickets', { auth: true });
  }

  // Get ticket by ID
  async getTicketById(ticketId) {
    return this.get(`/support/ticket/${ticketId}`, { auth: true });
  }

  // Reply to ticket
  async replyToTicket(ticketId, message) {
    return this.post(`/support/ticket/${ticketId}/reply`, {
      message
    }, { auth: true });
  }
    
}


// Create singleton instance
const apiService = new ApiService();

// Export for use in components
export default apiService;
